/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import {
  ListItem,
  OrderedList,
  PasswordInput,
  UnorderedList
} from 'carbon-components-react';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { ChannelContentProps } from '../../../util/types';
import { FIELDS } from '../schema';
import { ApplicationStepMessages as messages } from './messages';

export const ApplicationContent = ({
  isQuickSetup,
  readOnly
}: ChannelContentProps) => {
  const formMethods = useFormContext();
  const { registerInputField } = useFormHelpers(formMethods);

  const verificationTokenChangeState = useState<boolean>(false);
  const botAccessTokenChangeState = useState<boolean>(false);

  return (
    <>
      <OrderedList className="TabContent__subtitle">
        <ListItem>
          <FormattedMessage
            values={{
              basicInformation: (
                <strong>{messages.basicInformation.defaultMessage}</strong>
              ),
              appCredentials: (
                <strong>{messages.appCredentials.defaultMessage}</strong>
              )
            }}
            {...messages.stepOne}
          />
          <div className="TabContent__spacer16" />
          <PasswordInput
            {...registerInputField({
              field: FIELDS.VERIFICATION_TOKEN,
              isPasswordField: true,
              isQuickSetup,
              labelText: messages.verificationTokenLabel,
              placeholderText: messages.verificationTokenPlaceholder,
              changeState: verificationTokenChangeState,
              readOnly
            })}
            id="slack-verification-token"
          />
        </ListItem>
        <ListItem>
          <FormattedMessage
            values={{
              oauthAndPermissions: (
                <strong>{messages.oauthAndPermissions.defaultMessage}</strong>
              ),
              botTokenScopes: (
                <strong>{messages.botTokenScopes.defaultMessage}</strong>
              ),
              addAnOauthScope: (
                <strong>{messages.addAnOauthScope.defaultMessage}</strong>
              )
            }}
            {...messages.stepTwo}
          />
          <div className="TabContent__spacer8" />
          <UnorderedList className="TabContent__subtitle">
            <ListItem>
              <FormattedMessage {...messages.scopeOne} />
            </ListItem>
            <ListItem>
              <FormattedMessage {...messages.scopeTwo} />
            </ListItem>
            <ListItem>
              <FormattedMessage {...messages.scopeThree} />
            </ListItem>
            <ListItem>
              <FormattedMessage {...messages.scopeFour} />
            </ListItem>
            <ListItem>
              <FormattedMessage {...messages.scopeFive} />
            </ListItem>
          </UnorderedList>
        </ListItem>
        <ListItem>
          <FormattedMessage
            values={{
              oauthAndPermissions: (
                <strong>{messages.oauthAndPermissions.defaultMessage}</strong>
              ),
              installAppToWorkspace: (
                <strong>{messages.installAppToWorkspace.defaultMessage}</strong>
              ),
              allow: <strong>{messages.allow.defaultMessage}</strong>,
              botUserAccessTokenLabel: (
                <strong>
                  {messages.botUserAccessTokenLabel.defaultMessage}
                </strong>
              )
            }}
            {...messages.stepThree}
          />
          <div className="TabContent__spacer16" />
          <PasswordInput
            {...registerInputField({
              field: FIELDS.BOT_ACCESS_TOKEN,
              isPasswordField: true,
              isQuickSetup,
              labelText: messages.botUserAccessTokenLabel,
              placeholderText: messages.botAccessTokenPlaceholder,
              changeState: botAccessTokenChangeState,
              readOnly
            })}
            id="slack-bot-access-token"
          />
        </ListItem>
      </OrderedList>
      <div className="TabContent__spacer24" />
    </>
  );
};
